import { Injectable, computed, inject, signal } from '@angular/core';
import { AskEvaClient, AskEvaHistoryListResponse, AskEvaQueryRequest, AskEvaThreadResponse, AskEvaUserRatingRequest } from 'api-clients/ai-api-client';
import { EvaAnswerDto, EvaQuestionAnswer, EvaQuestionAnswerChatThread } from '../models/eva-question-answer.model';
@Injectable()
export class EvaQuestionAnswerService {
  readonly #askEvaClient: AskEvaClient = inject(AskEvaClient);

  // Cancels a superseded thread-history load so a late response can't overwrite a newer one
  // (EZ-188788). Dedicated controller — separate from processQuery's #abortController.
  #historyAbortController?: AbortController;

  readonly isCallingApi = signal(false);
  readonly isLoadingHistory = signal(false);
  readonly hasNewAnswer = signal(false);

  /** True while a confirmed HITL action's follow-up is in flight (EZ-186935 AC4: no Stop, no new question). */
  readonly #isProcessingAction = signal(false);
  readonly isProcessingAction = this.#isProcessingAction.asReadonly();

  #abortController?: AbortController;

  readonly questionAnswers = signal<EvaQuestionAnswerChatThread>({
    threadId: '',
    applicantId: null,
    questionAndAnswers: [],
  });
  readonly applicantIdFromThreadResponse = signal<number | null>(null);

  /** True while any answer has an unresolved HITL confirmation; gates the chat input (EZ-186935). */
  readonly hasPendingConfirmation = computed(() =>
    this.questionAnswers().questionAndAnswers.some(
      questionAnswer => questionAnswer.answer?.hitl?.required === true
        && (questionAnswer.answer.hitl.options?.length ?? 0) > 0
        && !questionAnswer.hitlSelectedOption
    )
  );

  abort(): void {
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  addStopConfirmation(): void {
    this.hasNewAnswer.set(true);
    this.questionAnswers.update((current) => {
      const next = [...current.questionAndAnswers];
      const index = next.findIndex(item => !item.answer?.requestId && !item.answer?.response);
      if (index === -1) return current;
      next[index] = {
        ...next[index],
        isNewResponse: true,
        answer: {
          requestId: '',
          threadId: current.threadId,
          response: "I've stopped generating the response. What would you like help with next?",
          shouldShowTextResponse: true,
          data: { applicants: [], policies: [] },
        },
      };
      return { ...current, questionAndAnswers: next };
    });
  }

  #removeCancelledQuestion(): void {
    const questionsToBeRemovedArray = this.questionAnswers()?.questionAndAnswers.filter(item => !item.answer?.requestId && !item.answer?.response);
    if (questionsToBeRemovedArray?.length <= 1) return;

    this.questionAnswers.update((current) => {
      const nextQuestionAndAnswers = current.questionAndAnswers.filter(item => item !== questionsToBeRemovedArray[0]);
      return {
        ...current,
        questionAndAnswers: nextQuestionAndAnswers,
      };
    });
  }

  async processQuery(request: AskEvaQueryRequest, options?: { isAction?: boolean }): Promise<EvaAnswerDto | undefined> {
    this.abort();
    this.#removeCancelledQuestion();
    this.#abortController = new AbortController();
    this.isCallingApi.set(true);
    // A HITL confirmation follow-up disables Stop + the input until it resolves (EZ-186935 AC4).
    if (options?.isAction) {
      this.#isProcessingAction.set(true);
    }
    let answered = false;
    try {
      const response = await this.#askEvaClient.processQuery(request, this.#abortController.signal) as EvaAnswerDto;
      answered = true;
      return this.#setNecessaryFieldsForQuestionAnswerDisplay(response);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return undefined;
      }
      throw error;
    } finally {
      this.#abortController = undefined;
      this.isCallingApi.set(false);
      this.#isProcessingAction.set(false);
      if (answered) this.hasNewAnswer.set(true);
    }
  }

  async setConversationUserRating(askEvaConversationId: number, rating: AskEvaUserRatingRequest): Promise<void> {
    if (!askEvaConversationId) {
      return;
    }

    await this.#askEvaClient.setConversationUserRating(askEvaConversationId, rating);
  }

  #setNecessaryFieldsForQuestionAnswerDisplay(answer: EvaAnswerDto): EvaAnswerDto {
    const hasData = !!answer.data;
    const applicantsCount = answer.data?.applicants?.length ?? 0;
    const policiesCount = answer.data?.policies?.length ?? 0;

    const shouldShowTextResponse = !!(
      answer.response && (!hasData || !applicantsCount && !policiesCount)
    );

    const primaryFieldNamesInData = hasData ? Object.keys(answer.data) : [];
    return {
      ...answer,
      primaryFieldNamesInData,
      shouldShowTextResponse,
    };
  }

  /** Record the user's HITL choice for an answer, locking its option set. */
  confirmActionSelection(entryIndex: number, option: string): void {
    this.questionAnswers.update((current) => {
      const next = [...current.questionAndAnswers];
      const entry = next[entryIndex];
      if (!entry?.answer?.hitl?.required || entry.hitlSelectedOption) {
        return current;
      }
      next[entryIndex] = { ...entry, hitlSelectedOption: option };
      return { ...current, questionAndAnswers: next };
    });
  }

  addQuestion(question: string, applicantId?: number | null): void {
    const trimmed = question.trim();
    if (!trimmed) {
      return;
    }

    this.questionAnswers.update((current): EvaQuestionAnswerChatThread => {
      const nextApplicantId = applicantId !== undefined ? applicantId : current.applicantId;
      const placeholderAnswer: EvaAnswerDto = {
        requestId: '',
        threadId: current.threadId,
        response: '',
        data: {
          applicants: [],
          policies: [],
        },
      };

      return {
        threadId: current.threadId,
        applicantId: nextApplicantId,
        questionAndAnswers: [...current.questionAndAnswers, { question: trimmed, answer: placeholderAnswer, questionDate: new Date() }],
      };
    });
  }

  updateQuestionAnswers(question: string, answer: EvaAnswerDto, applicantId?: number | null): void {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      return;
    }

    this.questionAnswers.update((current): EvaQuestionAnswerChatThread => {
      const nextThreadId = answer.threadId || current.threadId;
      const nextApplicantId = applicantId !== undefined ? applicantId : current.applicantId;

      const nextQuestionAndAnswers: EvaQuestionAnswer[] = [...current.questionAndAnswers];
      let updateAnswerForExistingQuestion = false;
      const isNewResponse = this.hasNewAnswer();

      const indexOfQuestionWithNoAnswer = nextQuestionAndAnswers.findIndex(
        item =>
          !item.answer?.requestId &&
          !item.answer?.response
      );

      // only update answer for the matching unanswered question with empty requestId and response
      if (indexOfQuestionWithNoAnswer !== -1) {
        const existing = nextQuestionAndAnswers[indexOfQuestionWithNoAnswer];
        nextQuestionAndAnswers[indexOfQuestionWithNoAnswer] = {
          question: existing.question,
          answer,
          isNewResponse,
          questionDate: existing.questionDate ?? new Date(),
        };
        updateAnswerForExistingQuestion = true;
      }

      if (!updateAnswerForExistingQuestion) {
        nextQuestionAndAnswers.push({ question: trimmedQuestion, answer, isNewResponse, questionDate: new Date() });
      }

      return {
        threadId: nextThreadId,
        applicantId: nextApplicantId,
        questionAndAnswers: nextQuestionAndAnswers,
      };
    });
  }

  clearNewResponseFlags(): void {
    this.questionAnswers.update(current => ({
      ...current,
      questionAndAnswers: current.questionAndAnswers.map(item =>
        item.isNewResponse ? { ...item, isNewResponse: false } : item
      ),
    }));
  }

  resetQuestionAnswers(threadId: string, applicantId: number | null = null): void {
    this.#isProcessingAction.set(false);
    this.questionAnswers.set({
      threadId,
      applicantId,
      questionAndAnswers: [],
    });
  }

  setApplicantIdByQuestionAnswerIndex(entryIndex: number, applicantId: number): void {
    this.questionAnswers.update((current) => {
      const nextQuestionAndAnswers = [...current.questionAndAnswers];
      if (entryIndex >= 0 && entryIndex < nextQuestionAndAnswers.length) {
        nextQuestionAndAnswers[entryIndex] = { ...nextQuestionAndAnswers[entryIndex], applicantId };
      }
      return { ...current, questionAndAnswers: nextQuestionAndAnswers };
    });
  }

  setPolicyIdByQuestionAnswerIndex(entryIndex: number, policyId: number): void {
    this.questionAnswers.update((current) => {
      const nextQuestionAndAnswers = [...current.questionAndAnswers];
      if (entryIndex >= 0 && entryIndex < nextQuestionAndAnswers.length) {
        nextQuestionAndAnswers[entryIndex] = { ...nextQuestionAndAnswers[entryIndex], policyId };
      }
      return { ...current, questionAndAnswers: nextQuestionAndAnswers };
    });
  }

  #resolveApplicantId(
    applicantId: string | number | null | undefined,
    applicants: { applicantId?: number | null }[] | null | undefined,
    remainingItems: { applicantId?: string | number | null | undefined }[]
  ): number | null {
    if (applicantId) {
      return +applicantId;
    }
    if (applicants?.length) {
      const matchedEntry = remainingItems.find(
        entry => entry.applicantId != null
          && applicants.some(applicant => applicant.applicantId === +entry.applicantId!)
      );
      if (matchedEntry?.applicantId != null) {
        return +matchedEntry.applicantId;
      }
    }
    return null;
  }

  #resolvePolicyId(
    policyId: string | number | null | undefined,
    policies: { policyMasterID?: number | null }[] | null | undefined,
    remainingItems: { policyId?: string | number | null | undefined }[]
  ): number | null {
    if (policyId) {
      return +policyId;
    }
    if (policies?.length) {
      const matchedEntry = remainingItems.find(
        entry => entry.policyId != null
          && policies.some(policy => policy.policyMasterID === +entry.policyId!)
      );
      if (matchedEntry?.policyId != null) {
        return +matchedEntry.policyId;
      }
    }
    return null;
  }

  async loadThreadHistory(threadId?: string | null | undefined): Promise<void> {
    // Cancel any in-flight history load so quickly switching View chat targets can't land a stale
    // response over a newer one (EZ-188788).
    this.#historyAbortController?.abort();
    const controller = new AbortController();
    this.#historyAbortController = controller;
    this.isLoadingHistory.set(true);
    try {
      const response: AskEvaThreadResponse = await this.#askEvaClient.getThreadHistory(threadId, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      const data = response?.data ?? [];

      const questionAndAnswers: EvaQuestionAnswer[] = data.map((item, index) => {
        const answer: EvaAnswerDto = {
          requestId: item.answer?.requestId ?? '',
          threadId: response.threadId ?? '',
          askEvaConversationId: item.answer?.askEvaConversationId,
          response: item.answer?.response ?? '',
          data: item.answer?.data ?? { applicants: [], policies: [] },
          userRating: item.answer?.userRating,
          userRatingComment: item.answer?.userRatingComment,
          // Present only for an unanswered HITL confirmation restored from history; re-renders its options.
          hitl: item.answer?.hitl,
          context: item.answer?.context,
        };
        const questionAnswerEntry: EvaQuestionAnswer = {
          question: item.question ?? '',
          answer: this.#setNecessaryFieldsForQuestionAnswerDisplay(answer),
          isHistoryLoaded: true,
          questionDate: item.created ? new Date(item.created) : undefined,
        };
        if (item?.applicantId) {
          this.applicantIdFromThreadResponse.set(+item.applicantId);
        }
        // Backfill applicantId onto applicant-list entries for history restoration.
        // Traverse forward to find the next applicantId selection, skipping unrelated follow-up questions.
        // Only backfill if the found applicantId actually belongs to this entry's applicants list,
        // which handles: (1) unrelated questions between list and selection, (2) consecutive applicant lists.
        const resolvedApplicantId = this.#resolveApplicantId(item.applicantId, answer.data?.applicants, data.slice(index + 1));
        if (resolvedApplicantId != null) {
          questionAnswerEntry.applicantId = resolvedApplicantId;
        }

        const resolvedPolicyId = this.#resolvePolicyId(item.policyId, answer.data?.policies, data.slice(index + 1));
        if (resolvedPolicyId != null) {
          questionAnswerEntry.policyId = resolvedPolicyId;
        }

        return questionAnswerEntry;
      });

      // Always pin the panel to the requested thread — including an empty one — so a stale prior
      // conversation can't linger when a chat has no messages (EZ-188788).
      this.questionAnswers.set({
        threadId: response?.threadId ?? threadId ?? '',
        applicantId: questionAndAnswers.length ? this.applicantIdFromThreadResponse() : null,
        questionAndAnswers,
      });
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      throw error;
    } finally {
      if (this.#historyAbortController === controller) {
        this.isLoadingHistory.set(false);
        this.#historyAbortController = undefined;
      }
    }
  }

  async getHistory(): Promise<AskEvaHistoryListResponse> {
    return await this.#askEvaClient.getHistory();
  }
}
